import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Users, Trash2, Edit2, X, FileText, FolderOpen, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ProjectIcon from '../components/ProjectIcon';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Teams() {
  const { user } = useContext(AuthContext);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '',
    projectIds: []
  });

  // Reset form to initial state
  const resetTeamForm = () => {
    setFormData({
      name: '',
      description: '',
      projectIds: []
    });
  };
  const [editFormData, setEditFormData] = useState({ 
    name: '', 
    description: '',
    projectIds: []
  });
  const [selectedUserId, setSelectedUserId] = useState('');
  const [expandedTeams, setExpandedTeams] = useState({}); // Takım üyeleri expand state'i

  useEffect(() => {
    // ⚡ PERFORMANCE: Paralel API çağrıları
    Promise.all([
      fetchTeams(),
      fetchUsers(),
      fetchProjects()
    ]).catch(error => {
      console.error('❌ Veri yükleme hatası:', error);
    });
  }, []);
  
  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      const sorted = [...response.data].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
      setTeams(sorted);
    } catch (error) {
      toast.error('Takımlar yüklenemedi');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const toggleProject = (projectId, isEditMode = false) => {
    if (isEditMode) {
      setEditFormData(prev => ({
        ...prev,
        projectIds: prev.projectIds.includes(projectId)
          ? prev.projectIds.filter(id => id !== projectId)
          : [...prev.projectIds, projectId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        projectIds: prev.projectIds.includes(projectId)
          ? prev.projectIds.filter(id => id !== projectId)
          : [...prev.projectIds, projectId]
      }));
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/teams`, formData);
      toast.success('Takım oluşturuldu');
      setShowCreateDialog(false);
      resetTeamForm();
      
      // ✅ PWA için hard reload - cache bypass
      setTimeout(() => {
        window.location.href = window.location.href.split('?')[0] + '?_refresh=' + Date.now();
      }, 800);
    } catch (error) {
      console.error('Takım oluşturma hatası:', error.response?.data);
      toast.error('Takım oluşturulamadı: ' + (error.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedUserId) return;
    try {
      await axios.post(`${API}/teams/${selectedTeam.id}/members`, null, {
        params: { user_id: selectedUserId }
      });
      toast.success('Üye eklendi');
      setShowAddMemberDialog(false);
      setSelectedUserId('');
      fetchTeams();
      fetchUsers();
    } catch (error) {
      toast.error('Üye eklenemedi');
    }
  };

  const handleRemoveMember = async (teamId, userId, userName) => {
    if (!window.confirm(`${userName} kullanıcısını takımdan çıkarmak istediğinize emin misiniz?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/teams/${teamId}/members/${userId}`);
      toast.success('Üye takımdan çıkarıldı');
      fetchTeams();
      fetchUsers();
    } catch (error) {
      toast.error('Üye çıkarılamadı: ' + (error.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const getUserById = (userId) => {
    return users.find(u => u.id === userId);
  };

  const canEditTeam = (team) => {
    // Check if user has can_manage_teams permission
    return user?.permissions?.includes('can_manage_teams');
  };

  const handleEditTeam = (team) => {
    setSelectedTeam(team);
    setEditFormData({
      name: team.name,
      description: team.description || '',
      projectIds: team.projectIds || []
    });
    setShowEditDialog(true);
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/teams/${selectedTeam.id}`, editFormData);
      toast.success('Takım güncellendi');
      setShowEditDialog(false);
      fetchTeams();
    } catch (error) {
      toast.error('Takım güncellenemedi: ' + (error.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const handleDeleteTeam = async (team) => {
    if (!window.confirm(`"${team.name}" takımını silmek istediğinizden emin misiniz?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/teams/${team.id}`);
      toast.success('Takım silindi');
      
      // ✅ PWA için hard reload - cache bypass
      setTimeout(() => {
        window.location.href = window.location.href.split('?')[0] + '?_refresh=' + Date.now();
      }, 800);
    } catch (error) {
      toast.error('Takım silinemedi: ' + (error.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8 space-y-6" data-testid="teams-page">
      <div className="flex items-center justify-between mb-4 sm:mb-6 pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <span className="relative">
              Takımlar
              {/* Takım sayısı badge */}
              <span className="absolute -top-1 left-[100%] ml-1 min-w-[1.5rem] sm:min-w-[1.75rem] h-6 sm:h-7 px-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-md flex items-center justify-center shadow-lg whitespace-nowrap">
                {teams.length.toLocaleString('tr-TR')}
              </span>
            </span>
          </h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">
            Ekip üyelerinizi ve takımlarınızı yönetin
            {teams.length > 0 && (
              <span className="text-blue-600 font-medium text-xs sm:text-sm"> • {teams.length.toLocaleString('tr-TR')} takım</span>
            )}
          </p>
        </div>
        {user?.permissions?.includes('can_manage_teams') && (
          <Dialog open={showCreateDialog} onOpenChange={(open) => {
            setShowCreateDialog(open);
            if (open) resetTeamForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-cyan-600 to-blue-600" data-testid="create-team-button">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Takım
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Close Button */}
            <button
              onClick={() => setShowCreateDialog(false)}
              className="absolute right-3 top-3 z-50 p-1.5 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
            >
              <X className="w-4 h-4 text-gray-600 hover:text-gray-900" />
            </button>
            
            <DialogHeader className="bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700 -m-6 mb-0 px-6 py-4 rounded-t-lg">
              <DialogTitle className="text-xl font-bold text-white">Yeni Takım Oluştur</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleCreateTeam} className="space-y-6 py-6 px-1">
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-5 rounded-xl border-2 border-cyan-200">
                <Label htmlFor="name" className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <Users className="w-5 h-5 text-cyan-600" />
                  Takım Adı <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Örn: Frontend Ekibi"
                  className="mt-3 bg-white border-2 border-cyan-300 focus:border-cyan-500 focus:ring-cyan-500"
                  required
                  data-testid="team-name-input"
                />
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl border-2 border-purple-200">
                <Label htmlFor="description" className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  Açıklama
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Takımın görev tanımı ve sorumluluklarını belirtin..."
                  className="mt-3 bg-white border-2 border-purple-300 focus:border-purple-500 focus:ring-purple-500"
                  rows={3}
                  data-testid="team-description-input"
                />
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl border-2 border-green-200">
                <Label className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-green-600" />
                  Atanacağı Projeler <span className="text-red-500">*</span>
                </Label>
                <p className="text-sm text-gray-600 mt-2 ml-9">
                  Takımın çalışabileceği projeleri seçin (en az 1 proje gerekli)
                </p>
                <div className="mt-3 space-y-2 max-h-60 overflow-y-auto bg-white border-2 border-green-300 rounded-xl p-3 custom-scrollbar">
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <label
                        key={project.id}
                        className="flex items-start gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all duration-200 hover:border-green-400 hover:bg-green-50"
                      >
                        <Checkbox
                          id={`create-project-${project.id}`}
                          checked={formData.projectIds.includes(project.id)}
                          onCheckedChange={() => toggleProject(project.id, false)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{project.name}</div>
                          {project.description && (
                            <div className="text-xs text-gray-500 mt-1">{project.description}</div>
                          )}
                        </div>
                      </label>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-4">Henüz proje yok</p>
                  )}
                </div>
                <p className="text-xs font-semibold text-cyan-700 mt-3 flex items-center gap-2 ml-9">
                  <CheckCircle className="w-4 h-4" />
                  Seçili: {formData.projectIds.length} proje
                </p>
              </div>
              
              <div className="bg-gray-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg border-t-2 border-gray-200 flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="lg"
                  onClick={() => setShowCreateDialog(false)}
                  className="flex-1 border-2 hover:bg-gray-100"
                >
                  <X className="w-4 h-4 mr-2" />
                  İptal
                </Button>
                <Button 
                  type="submit" 
                  size="lg"
                  className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-semibold shadow-lg" 
                  data-testid="submit-team-button"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Oluştur
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {teams.map((team) => (
          <div key={team.id} className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all border border-gray-100 hover:border-blue-200 p-6" data-testid={`team-card-${team.id}`}>
            {/* Header with Action Buttons */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-lg text-gray-900 mb-1 line-clamp-2">{team.name}</h3>
                  {team.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{team.description}</p>
                  )}
                </div>
              </div>
              
              {/* Action Icons - Top Right */}
              <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                {canEditTeam(team) && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditTeam(team)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      data-testid={`edit-team-${team.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteTeam(team)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-team-${team.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Project Icons Row - Below header, full width to avoid overlapping title */}
            {team.projectIds && team.projectIds.length > 0 && (() => {
              const linked = team.projectIds
                .map(pid => projects.find(p => p.id === pid))
                .filter(Boolean);
              if (!linked.length) return null;
              return (
                <div className="flex items-center gap-1.5 flex-wrap justify-end mb-1" data-testid={`team-project-icons-${team.id}`}>
                  {linked.map(proj => (
                    <ProjectIcon
                      key={proj.id}
                      project={proj}
                      size="md"
                      className="ring-2 ring-white"
                    />
                  ))}
                </div>
              );
            })()}

            {/* Team Info */}
            <div className="pt-4 border-t border-gray-100 space-y-4">
              {/* Takım Liderleri - Birden fazla lider destekli */}
              {(() => {
                // Hem eski leaderId hem yeni leaderIds'i destekle
                const leaderIds = team.leaderIds && team.leaderIds.length > 0 
                  ? team.leaderIds 
                  : (team.leaderId ? [team.leaderId] : []);
                
                // Sadece var olan liderleri filtrele
                const validLeaders = leaderIds.map(id => getUserById(id)).filter(Boolean);
                
                if (validLeaders.length === 0) return null;
                
                return (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                        <Users className="w-3.5 h-3.5 text-white" />
                      </div>
                      <span>Takım Lider{validLeaders.length > 1 ? 'leri' : 'i'} ({validLeaders.length})</span>
                    </div>
                    <div className="space-y-1 ml-8">
                      {validLeaders.map(leader => (
                        <div key={leader.id} className="flex items-center gap-2 text-sm text-gray-700 bg-purple-50 rounded-lg px-3 py-2">
                          {leader.picture && (
                            <img src={leader.picture} alt={leader.name} className="w-6 h-6 rounded-full" />
                          )}
                          <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                          <span className="font-medium">{leader.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              
              {/* Takım Üyeleri */}
              <div>
                {(() => {
                  // Sadece gerçekten var olan (aktif) üyeleri say
                  const activeMembers = (team.memberIds || []).filter(memberId => getUserById(memberId));
                  const activeMemberCount = activeMembers.length;
                  
                  return (
                    <>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                        <Users className="w-4 h-4 text-blue-600" />
                        <span className="font-medium">Takım Üyeleri ({activeMemberCount})</span>
                      </div>
                      
                      {/* Member Names */}
                      {activeMemberCount > 0 ? (
                        <div className="space-y-1">
                          {/* Gösterilecek üyeleri belirle */}
                          {(() => {
                            const isExpanded = expandedTeams[team.id];
                            const displayMembers = isExpanded ? activeMembers : activeMembers.slice(0, 5);
                            
                            return displayMembers.map((memberId) => {
                              const member = getUserById(memberId);
                              return member ? (
                                <div key={memberId} className="flex items-center justify-between text-xs text-gray-600 group hover:bg-gray-50 rounded px-2 py-1.5 transition-colors">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    {member.picture && (
                                      <img src={member.picture} alt={member.name} className="w-5 h-5 rounded-full flex-shrink-0" />
                                    )}
                                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full flex-shrink-0"></span>
                                    <span className="truncate">{member.name}</span>
                                  </div>
                                  {user?.permissions?.includes('can_manage_teams') && (
                                    <button
                                      onClick={() => handleRemoveMember(team.id, memberId, member.name)}
                                      className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                                      title="Üyeyi çıkar"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              ) : null;
                            });
                          })()}
                          
                          {/* Expandable Button */}
                          {activeMemberCount > 5 && (
                            <button
                              onClick={() => setExpandedTeams(prev => ({
                                ...prev,
                                [team.id]: !prev[team.id]
                              }))}
                              className="w-full flex items-center justify-center gap-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg px-3 py-2 transition-all duration-200 font-medium"
                            >
                              {expandedTeams[team.id] ? (
                                <>
                                  <ChevronUp className="w-3.5 h-3.5" />
                                  Daha az göster
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="w-3.5 h-3.5" />
                                  {activeMemberCount - 5} üye daha göster
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">Henüz üye yok</p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader className="bg-gradient-to-r from-green-600 via-emerald-600 to-green-700 -m-6 mb-0 px-6 py-4 rounded-t-lg">
            <DialogTitle className="text-xl font-bold text-white">{selectedTeam?.name} - Üye Ekle</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddMember} className="space-y-6 py-6 px-1">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl border-2 border-green-200">
              <Label className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <Users className="w-5 h-5 text-green-600" />
                Kullanıcı Seç <span className="text-red-500">*</span>
              </Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger data-testid="select-user-trigger" className="mt-3 bg-white border-2 border-green-300">
                  <SelectValue placeholder="Kullanıcı seçin" />
                </SelectTrigger>
                <SelectContent>
                  {users
                    .filter(u => !selectedTeam?.memberIds.includes(u.id))
                    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'))
                    .map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.email})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="bg-gray-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg border-t-2 border-gray-200 flex gap-3">
              <Button 
                type="button" 
                variant="outline" 
                size="lg"
                onClick={() => setShowAddMemberDialog(false)}
                className="flex-1 border-2 hover:bg-gray-100"
              >
                <X className="w-4 h-4 mr-2" />
                İptal
              </Button>
              <Button 
                type="submit" 
                size="lg"
                className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold shadow-lg" 
                data-testid="submit-member-button"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Ekle
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* Close Button */}
          <button
            onClick={() => setShowEditDialog(false)}
            className="absolute right-3 top-3 z-50 p-1.5 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
          >
            <X className="w-4 h-4 text-gray-600 hover:text-gray-900" />
          </button>
          
          <DialogHeader className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 -m-6 mb-0 px-6 py-4 rounded-t-lg">
            <DialogTitle className="text-xl font-bold text-white">Takımı Düzenle</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdateTeam} className="space-y-6 py-6 px-1">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border-2 border-blue-200">
              <Label htmlFor="edit-name" className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Takım Adı <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                className="mt-3 bg-white border-2 border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl border-2 border-purple-200">
              <Label htmlFor="edit-description" className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Açıklama
              </Label>
              <Textarea
                id="edit-description"
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                className="mt-3 bg-white border-2 border-purple-300 focus:border-purple-500 focus:ring-purple-500"
                rows={3}
              />
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl border-2 border-green-200">
              <Label className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-green-600" />
                Atanacağı Projeler <span className="text-red-500">*</span>
              </Label>
              <p className="text-sm text-gray-600 mt-2 ml-9">
                Takımın çalışabileceği projeleri seçin (en az 1 proje gerekli)
              </p>
              <div className="mt-3 space-y-2 max-h-60 overflow-y-auto bg-white border-2 border-green-300 rounded-xl p-3 custom-scrollbar">
                {projects.length > 0 ? (
                  projects.map((project) => (
                    <label
                      key={project.id}
                      className="flex items-start gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all duration-200 hover:border-green-400 hover:bg-green-50"
                    >
                      <Checkbox
                        id={`edit-project-${project.id}`}
                        checked={editFormData.projectIds.includes(project.id)}
                        onCheckedChange={() => toggleProject(project.id, true)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{project.name}</div>
                        {project.description && (
                          <div className="text-xs text-gray-500 mt-1">{project.description}</div>
                        )}
                      </div>
                    </label>
                  ))
                ) : (
                  <p className="text-sm text-gray-400 text-center py-4">Henüz proje yok</p>
                )}
              </div>
              <p className="text-xs font-semibold text-cyan-700 mt-3 flex items-center gap-2 ml-9">
                <CheckCircle className="w-4 h-4" />
                Seçili: {editFormData.projectIds.length} proje
              </p>
            </div>
            
            <div className="bg-gray-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg border-t-2 border-gray-200 flex gap-3">
              <Button 
                type="button" 
                variant="outline" 
                size="lg"
                onClick={() => setShowEditDialog(false)}
                className="flex-1 border-2 hover:bg-gray-100"
              >
                <X className="w-4 h-4 mr-2" />
                İptal
              </Button>
              <Button 
                type="submit" 
                size="lg"
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold shadow-lg"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Güncelle
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
